import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Children,
  createContext,
  isValidElement,
  KeyboardEvent,
  ReactElement,
  cloneElement,
  useContext,
} from 'react';
import {
  ImperativePanelHandle,
  PanelGroup,
  PanelGroupProps,
} from 'react-resizable-panels';
import { WbDockablePanel } from './WbDockablePanel';
import { useResponsiveMobile } from '../../hooks';

export type DockDirection = 'vertical' | 'horizontal';

export interface DockableGroupContextValue {
  isMobile: boolean;
  isPrimaryCollapsed: boolean;
  isPrimaryFullyExpanded: boolean;
  setPrimaryFullyExpanded: (value: boolean) => void;
  setPrimaryCollapsed: (v: boolean) => void;
  registerPanel: (index: number, ref: ImperativePanelHandle | null) => void;
  resetToDefaults: () => void;
  direction: DockDirection;
  primaryIndex: number;
  panelCount: number;
  expandOnResize?: boolean;
}

export const DockableGroupContext =
  createContext<DockableGroupContextValue | null>(null);
export const useDockableGroupContext = () => useContext(DockableGroupContext);

/*
* Note: This component assumes that all direct children of type WbDockablePanel are siblings (i.e. no nesting of PanelGroups or other components between them) and that their order doesn't change dynamically. 
This allows it to assign indices to panels based on their order in the children and manage state accordingly.
*/
export const WbDockableGroup: FC<
  PanelGroupProps & {
    isSidebarCollapsed?: boolean;
    onSidebarCollapsedChange?: (v: boolean) => void;
    expandOnResize?: boolean;
  }
> = ({
  children,
  isSidebarCollapsed,
  onSidebarCollapsedChange,
  expandOnResize = true,
  ...props
}) => {
  const panelRefs = useRef<(ImperativePanelHandle | null)[]>([null, null]);
  const isMobile = useResponsiveMobile();

  const [internalCollapsed, setInternalCollapsed] = useState(() => isMobile);
  const [isPrimaryFullyExpanded, setPrimaryFullyExpanded] = useState(false);
  const isControlled = isSidebarCollapsed !== undefined;
  const isPrimaryCollapsed = isControlled
    ? isSidebarCollapsed
    : internalCollapsed;

  /*
   * Determine primary panel index and compute default sizes> Rules for determining primary panel:
   * 1. If a panel has the `primary` prop set to true, it is the primary panel.
   * 2. If multiple panels have `primary` set to true, the first one is the primary panel.
   * 3. If no panels have `primary` set, the panel with the smallest default size is the primary panel.
   * 4. If no panels have `primary` set and all panels have equal default size (or no default size), the first panel is the primary panel.
   *
   * The primary panel is the one that will be collapsed/expanded when toggling the sidebar, and its state will be managed by this component.
   */
  const { primaryIndex, defaultSizes, panelCount } = useMemo(() => {
    const panels: Array<{ index: number; defaultSize?: number }> = [];
    let explicitPrimaryIdx: number | undefined;

    Children.forEach(children, child => {
      if (!isValidElement(child) || child.type !== WbDockablePanel) return;
      const currentIndex = panels.length;
      if ((child as ReactElement).props.primary === true)
        explicitPrimaryIdx = currentIndex;
      panels.push({
        index: currentIndex,
        defaultSize: (child as ReactElement).props.defaultSize,
      });
    });

    const panelsWithoutDefaultSize = panels.filter(
      p => p.defaultSize === undefined,
    );
    const remainingSize = Math.max(
      0,
      100 - panels.reduce((acc, p) => acc + (p.defaultSize ?? 0), 0),
    );
    const implicitSize =
      panelsWithoutDefaultSize.length > 0
        ? remainingSize / panelsWithoutDefaultSize.length
        : 100 / (panels.length || 1);
    const sizes = panels.map(p => p.defaultSize ?? implicitSize);

    if (explicitPrimaryIdx !== undefined)
      return {
        primaryIndex: explicitPrimaryIdx,
        defaultSizes: sizes,
        panelCount: panels.length,
      };

    let minSize = Infinity;
    let minIdx = 0;
    panels.forEach(p => {
      const effSize = p.defaultSize ?? implicitSize;
      if (effSize < minSize) {
        minSize = effSize;
        minIdx = p.index;
      }
    });

    return {
      primaryIndex: minIdx,
      defaultSizes: sizes,
      panelCount: panels.length,
    };
  }, [children]);

  const registerPanel = useCallback(
    (index: number, ref: ImperativePanelHandle | null) => {
      panelRefs.current[index] = ref;
    },
    [],
  );

  const setPrimaryCollapsed = useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalCollapsed(value);
      onSidebarCollapsedChange?.(value);
    },
    [isControlled, onSidebarCollapsedChange],
  );

  const defaultSizesRef = useRef(defaultSizes);
  defaultSizesRef.current = defaultSizes;

  useEffect(() => {
    setPrimaryFullyExpanded(false);
  }, [primaryIndex]);

  // Function to reset all panels to their default sizes
  const resetToDefaults = useCallback(() => {
    if (isPrimaryCollapsed) setPrimaryCollapsed(false);
    setTimeout(() => {
      defaultSizesRef.current.forEach((size, index) => {
        panelRefs.current[index]?.resize(size);
      });
    }, 0);
  }, [isPrimaryCollapsed, setPrimaryCollapsed]);

  // Handle breakpoint changes between Desktop and Mobile
  const prevIsMobile = useRef(isMobile);
  useEffect(() => {
    const wasMobile = prevIsMobile.current;
    prevIsMobile.current = isMobile;
    if (wasMobile === isMobile) return undefined;

    if (isMobile) {
      if (!isControlled) setInternalCollapsed(true);
      onSidebarCollapsedChange?.(true);
      return undefined;
    }
    if (!isControlled) setInternalCollapsed(false);
    onSidebarCollapsedChange?.(false);

    const timer = setTimeout(() => {
      const primaryRef = panelRefs.current[primaryIndex];
      const defaultSize = defaultSizesRef.current[primaryIndex];

      if (primaryRef && defaultSize !== undefined) {
        primaryRef.expand();
        requestAnimationFrame(() => {
          primaryRef.resize(defaultSize);
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [isMobile, isControlled, onSidebarCollapsedChange, primaryIndex]);

  // Synchronize panels on mobile when primary panel collapses/expands
  const prevIsPrimaryCollapsedRef = useRef(isPrimaryCollapsed);
  useEffect(() => {
    const prev = prevIsPrimaryCollapsedRef.current;
    prevIsPrimaryCollapsedRef.current = isPrimaryCollapsed;
    if (
      !isMobile ||
      prev === isPrimaryCollapsed ||
      prevIsMobile.current !== isMobile
    )
      return;

    const contentIdx = panelRefs.current.findIndex(
      (_, i) => i !== primaryIndex,
    );
    if (isPrimaryCollapsed) {
      panelRefs.current[contentIdx]?.expand();
    } else {
      panelRefs.current[contentIdx]?.collapse();
      panelRefs.current[primaryIndex]?.expand();
      panelRefs.current[primaryIndex]?.resize(100);
    }
  }, [isPrimaryCollapsed, isMobile, primaryIndex]);

  const ctx = useMemo<DockableGroupContextValue>(
    () => ({
      isMobile,
      isPrimaryCollapsed,
      isPrimaryFullyExpanded,
      setPrimaryFullyExpanded,
      setPrimaryCollapsed,
      registerPanel,
      resetToDefaults,
      direction: props.direction,
      primaryIndex,
      panelCount,
      expandOnResize,
    }),
    [
      isMobile,
      isPrimaryCollapsed,
      isPrimaryFullyExpanded,
      setPrimaryFullyExpanded,
      setPrimaryCollapsed,
      registerPanel,
      resetToDefaults,
      props.direction,
      primaryIndex,
      panelCount,
      expandOnResize,
    ],
  );

  // Toggle primary panel collapse state when Enter key is pressed on separator
  const handleKeyDownCapture = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    const handle = (e.target as HTMLElement | null)?.closest(
      '.handle-outer, [role="separator"]',
    );
    if (!handle) return;

    // find the closest panel group to the handle that was interacted with, and ensure it's the same one as the current context (in case of nested groups)
    const closestGroup = handle.closest('[data-panel-group]');
    const currentGroupContainer =
      e.currentTarget.querySelector('[data-panel-group]');

    if (closestGroup !== currentGroupContainer) return;

    e.preventDefault();
    e.stopPropagation();

    const nextCollapsed = !isPrimaryCollapsed;
    setPrimaryCollapsed(nextCollapsed);

    // When expanding, resize primary panel to full width
    if (!nextCollapsed) {
      requestAnimationFrame(() => {
        panelRefs.current[primaryIndex]?.resize(100);
      });
    }
  };

  return (
    <DockableGroupContext.Provider value={ctx}>
      <div
        style={{ display: 'contents' }}
        onKeyDownCapture={handleKeyDownCapture}
      >
        <PanelGroup {...props} data-panel-group>
          {(() => {
            let panelIndex = 0;
            return Children.map(children, child => {
              if (!isValidElement(child) || child.type !== WbDockablePanel)
                return child;
              const currentPanelIndex = panelIndex;
              panelIndex += 1;
              return cloneElement(child as ReactElement<{ index: number }>, {
                index: currentPanelIndex,
              });
            });
          })()}
        </PanelGroup>
      </div>
    </DockableGroupContext.Provider>
  );
};
